import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["icon"],
  _excluded2 = ["className", "details", "disabled", "header", "icon", "id"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { arrayOf, bool, func, node, objectOf, shape, string } from 'prop-types';
import { useControlled } from '@material-ui/core/utils';
import { AccordionSummary as MuiExpansionPanelSummary, AccordionDetails as MuiExpansionPanelDetails, Accordion as MuiExpansionPanel } from '@material-ui/core';
import AccordionExpandIcon from './AccordionExpandIcon';
import useAccordionStyles, { useExpansionPanelStyles, useExpansionPanelDetailsStyles, useExpansionPanelSummaryStyles } from './Accordion.styles';
import AccordionTransition from './AccordionTransition';
const Accordion = /*#__PURE__*/forwardRef(function Accordion(props, ref) {
  const {
    className,
    data,
    defaultExpanded,
    disabled,
    expanded: expandedProp,
    multiple,
    onChange
  } = props;
  const [expanded, setExpanded] = useControlled({
    controlled: expandedProp,
    default: defaultExpanded,
    name: 'Accordion'
  });
  const accordionClasses = useAccordionStyles();
  const _useExpansionPanelSum = useExpansionPanelSummaryStyles(),
    {
      icon: summaryIconClass
    } = _useExpansionPanelSum,
    summaryClasses = _objectWithoutProperties(_useExpansionPanelSum, _excluded);
  const expansionPanelClasses = useExpansionPanelStyles();
  const expansionPanelDetailsClasses = useExpansionPanelDetailsStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classNames('pds-accordion', accordionClasses.root, className),
    ref: ref
  }, data.map(function (item) {
    const {
        className: itemClassName,
        details,
        disabled: itemDisabled,
        header,
        icon,
        id
      } = item,
      otherProps = _objectWithoutProperties(item, _excluded2);
    return /*#__PURE__*/React.createElement(MuiExpansionPanel, Object.assign({}, otherProps, {
      key: id,
      className: classNames('pds-accordion__section', itemClassName),
      classes: expansionPanelClasses,
      defaultExpanded: defaultExpanded && defaultExpanded[id],
      disabled: disabled || itemDisabled,
      elevation: 0,
      expanded: !!(expanded && expanded[id]),
      onChange: function (e, isExpanded) {
        const newState = _objectSpread(_objectSpread({}, multiple ? expanded : {}), {}, {
          [id]: isExpanded
        });
        setExpanded(newState);
        if (onChange) {
          onChange(id, isExpanded);
        }
      },
      TransitionComponent: AccordionTransition,
      square: true
    }), /*#__PURE__*/React.createElement(MuiExpansionPanelSummary, {
      className: "pds-accordion__section-header",
      classes: summaryClasses,
      expandIcon: /*#__PURE__*/React.createElement(AccordionExpandIcon, {
        className: "pds-accordion__expand-icon"
      }),
      id: `${id}-header`,
      "aria-controls": `${id}-detail`
    }, icon && /*#__PURE__*/React.createElement("span", {
      className: classNames('pds-accordion__section-header-icon', summaryIconClass)
    }, icon), header), /*#__PURE__*/React.createElement(MuiExpansionPanelDetails, {
      className: "pds-accordion__section-body",
      classes: expansionPanelDetailsClasses
    }, details));
  }));
});
Accordion.propTypes = {
  /**
   * The CSS class that will be added to the root node of the component.
   */
  className: string,
  /**
   * Array of objects that contains information to be used in each section of the `Accordion`.
   */
  data: arrayOf(shape({
    className: string,
    details: node,
    disabled: bool,
    header: node.isRequired,
    icon: node,
    id: string.isRequired
  })).isRequired,
  /**
   * If the component is in uncontrolled mode you can use this prop to
   * decide which sections of the Accordion are expanded when the component
   * is mounted.
   *
   * The `key` is the ID of the section and the value is a boolean.
   */
  defaultExpanded: objectOf(bool),
  /**
   * Whether or not the ExpansionPanels in the Accordion can be interacted
   * with. If set to true, this will take precedence over the individual
   * `disabled` props for the Expansion Panels.
   */
  disabled: bool,
  /**
   * If the component is in controlled mode you can use this prop to decide
   * which sections of the Accordion should be expanded.
   *
   * If you pass the prop make sure you also set the `onChange` prop otherwise
   * the state won't change when the user tries to toggle any of the sections.
   */
  expanded: objectOf(bool),
  /**
   * If `true` the component could have more than one section expanded.
   *
   * **Only used if the component is in uncontrolled mode.** When it is
   * controlled you are responsible of handling that behavior.
   */
  multiple: bool,
  /**
   * Callback called when one of the sections of the accordion is toggled.
   *
   * @param {String} id the `id` field from the item that is getting toggled.
   * @param {boolean} expanded `true` if the section is getting expanded and `false` if it is being closed.
   */
  onChange: func
};
export default Accordion;